/**
 * StyleResolver - Converts component props to CSS styles
 *
 * Handles:
 * - Spacing (padding, margin) with units
 * - Dimensions (width, height, minWidth, minHeight, maxWidth, maxHeight) with units and 'auto'
 * - Responsive values (base/tablet/desktop breakpoints)
 * - Borders (width, style, color, radius)
 * - Typography (font, size, weight, etc.)
 * - Colors and backgrounds
 */

import { getComponentDefinition } from '../componentDefinitions';
import { colorValueToCss, colorValueToSolidCss } from '../fields/ColorPicker/colorUtils';
import { getFlexChildStyles } from '../fields/FlexChildField';

/**
 * Gap size mappings
 */
const GAP_SIZES = {
  none: '0px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

/**
 * Format a spacing value (padding/margin side)
 * @param {number|string} value - The value (number or 'auto')
 * @param {string} unit - CSS unit (px, rem, %, etc.)
 * @returns {string} Formatted CSS value
 */
export function formatSpacingValue(value, unit = 'px') {
  if (value === 'auto') return 'auto';
  if (value === 0 || value === '0') return '0';
  return `${value}${unit}`;
}

/**
 * Format a dimension value (width, height, etc.)
 * @param {Object|number|string} dimension - Dimension object { value, unit } or primitive
 * @returns {string} Formatted CSS value
 */
export function formatDimension(dimension) {
  if (dimension == null || dimension === '') return 'auto';

  // Handle responsive format - get base value
  if (dimension.base !== undefined) {
    return formatDimension(dimension.base);
  }

  // Handle object format { value, unit }
  if (typeof dimension === 'object' && dimension !== null) {
    if (dimension.value === 'auto' || dimension.value === undefined) return 'auto';
    return `${dimension.value}${dimension.unit != null ? dimension.unit : 'px'}`;
  }

  // Handle primitive
  if (dimension === 'auto') return 'auto';
  if (typeof dimension === 'number') return `${dimension}px`;
  return dimension;
}

/**
 * Get responsive dimension for a specific breakpoint
 * @param {Object} dimension - Responsive dimension { base, tablet, desktop }
 * @param {string} breakpoint - Target breakpoint
 * @returns {string} Formatted CSS value
 */
export function getResponsiveDimension(dimension, breakpoint = 'base') {
  if (!dimension) return 'auto';

  // Not in responsive format
  if (dimension.base === undefined) {
    return formatDimension(dimension);
  }

  // Get value for breakpoint with fallback to base
  const value = dimension[breakpoint] != null ? dimension[breakpoint] : dimension.base;
  return formatDimension(value);
}

/**
 * Get responsive direction for a specific breakpoint
 * @param {Object|string} direction - Responsive direction or string
 * @param {string} breakpoint - Target breakpoint
 * @returns {string} 'horizontal' or 'vertical'
 */
export function getResponsiveDirection(direction, breakpoint = 'base') {
  if (!direction) return 'horizontal';

  // String format
  if (typeof direction === 'string') return direction;

  // Responsive format
  if (direction.base !== undefined) {
    return direction[breakpoint] || direction.base || 'horizontal';
  }

  return 'horizontal';
}

/**
 * Get responsive spacing for a specific breakpoint
 * @param {Object} spacing - Spacing object (responsive or legacy format)
 * @param {string} breakpoint - Target breakpoint (base/tablet/desktop)
 * @returns {Object} Spacing object { top, right, bottom, left, unit }
 */
export function getResponsiveSpacing(spacing, breakpoint = 'base') {
  if (!spacing) return null;

  // Check if it's in responsive format (has base key)
  if (spacing.base !== undefined) {
    // Get value for breakpoint with fallback to base
    return spacing[breakpoint] != null ? spacing[breakpoint] : spacing.base;
  }

  // Legacy format - return as-is
  return spacing;
}

/**
 * Convert spacing object to CSS properties
 * @param {Object} spacing - Spacing object { top, bottom, left, right, unit } or responsive format
 * @param {string} property - 'padding' or 'margin'
 * @param {string} breakpoint - Target breakpoint for responsive spacing
 * @returns {Object} CSS style object
 */
export function resolveSpacing(spacing, property = 'padding', breakpoint = 'base') {
  if (!spacing) return {};

  // Get spacing for the current breakpoint
  const spacingObj = getResponsiveSpacing(spacing, breakpoint);
  if (!spacingObj) return {};

  const unit = spacingObj.unit || 'px';

  return {
    [`${property}Top`]: formatSpacingValue(spacingObj.top, unit),
    [`${property}Right`]: formatSpacingValue(spacingObj.right, unit),
    [`${property}Bottom`]: formatSpacingValue(spacingObj.bottom, unit),
    [`${property}Left`]: formatSpacingValue(spacingObj.left, unit),
  };
}

/**
 * Format full spacing CSS string (for media queries)
 * @param {Object} spacingObj - Spacing object { top, right, bottom, left, unit }
 * @param {string} property - 'padding' or 'margin'
 * @returns {string} CSS declarations string
 */
export function formatSpacingCSS(spacingObj, property = 'padding') {
  if (!spacingObj) return '';

  const unit = spacingObj.unit || 'px';
  const top = formatSpacingValue(spacingObj.top, unit);
  const right = formatSpacingValue(spacingObj.right, unit);
  const bottom = formatSpacingValue(spacingObj.bottom, unit);
  const left = formatSpacingValue(spacingObj.left, unit);

  return `${property}: ${top} ${right} ${bottom} ${left} !important;`;
}

/**
 * Convert border props to CSS properties
 * @param {Object} props - Component props containing border properties
 * @returns {Object} CSS style object
 */
export function resolveBorder(props) {
  const styles = {};

  const {
    borderWidth = 0,
    borderStyle = 'solid',
    borderColor = 'transparent',
    borderRadius = 0,
    // Per-corner radius
    radiusTopLeft,
    radiusTopRight,
    radiusBottomLeft,
    radiusBottomRight,
  } = props;

  // Only add border if width > 0
  if (borderWidth > 0) {
    // Use colorValueToSolidCss for border color (gradients not supported for borders)
    const resolvedBorderColor = colorValueToSolidCss(borderColor, 'transparent');
    styles.border = `${borderWidth}px ${borderStyle} ${resolvedBorderColor}`;
  }

  // Handle border radius
  if (radiusTopLeft !== undefined || radiusTopRight !== undefined ||
      radiusBottomLeft !== undefined || radiusBottomRight !== undefined) {
    // Per-corner radius
    styles.borderRadius = `${radiusTopLeft || borderRadius || 0}px ${radiusTopRight || borderRadius || 0}px ${radiusBottomRight || borderRadius || 0}px ${radiusBottomLeft || borderRadius || 0}px`;
  } else if (borderRadius > 0) {
    styles.borderRadius = `${borderRadius}px`;
  }

  return styles;
}

/**
 * Resolve all sizing properties (width, height, min/max) for a specific breakpoint
 * @param {Object} props - Component props containing sizing properties
 * @param {string} breakpoint - Target breakpoint (base/tablet/desktop)
 * @returns {Object} CSS style object with sizing properties
 */
export function resolveSizing(props, breakpoint = 'base') {
  if (!props) return {};
  const styles = {};

  if (props.width) {
    styles.width = getResponsiveDimension(props.width, breakpoint);
  }
  if (props.height) {
    styles.height = getResponsiveDimension(props.height, breakpoint);
  }
  if (props.minWidth) {
    const v = getResponsiveDimension(props.minWidth, breakpoint);
    if (v !== 'auto') styles.minWidth = v;
  }
  if (props.minHeight) {
    const v = getResponsiveDimension(props.minHeight, breakpoint);
    if (v !== 'auto') styles.minHeight = v;
  }
  if (props.maxWidth) {
    const v = getResponsiveDimension(props.maxWidth, breakpoint);
    if (v !== 'auto') styles.maxWidth = v;
  }
  if (props.maxHeight) {
    const v = getResponsiveDimension(props.maxHeight, breakpoint);
    if (v !== 'auto') styles.maxHeight = v;
  }

  return styles;
}

/**
 * Convert typography props to CSS properties
 * @param {Object} props - Component props containing typography properties
 * @returns {Object} CSS style object
 */
export function resolveTypography(props) {
  const styles = {};

  const {
    fontFamily,
    fontSize,
    fontWeight,
    lineHeight,
    letterSpacing,
    color,
    textAlign,
    textDecoration,
    textTransform,
  } = props;

  if (fontFamily && fontFamily !== 'System Default') {
    styles.fontFamily = `'${fontFamily}', sans-serif`;
  }

  if (fontSize) {
    // Handle both legacy (number) and new responsive format
    if (typeof fontSize === 'number') {
      styles.fontSize = `${fontSize}px`;
    } else if (fontSize?.base) {
      // New responsive format - use base (mobile-first)
      const baseFontSize = fontSize.base;
      styles.fontSize = formatDimension(baseFontSize);
    }
  }

  if (fontWeight) {
    // Support both plain string and responsive object
    styles.fontWeight = typeof fontWeight === 'string' ? fontWeight : fontWeight?.base || 'normal';
  }

  if (lineHeight) {
    if (typeof lineHeight === 'number') {
      styles.lineHeight = lineHeight;
    } else if (lineHeight?.base) {
      styles.lineHeight = formatDimension(lineHeight.base);
    }
  }

  if (letterSpacing) {
    if (typeof letterSpacing === 'number') {
      styles.letterSpacing = `${letterSpacing}px`;
    } else if (letterSpacing?.base) {
      styles.letterSpacing = formatDimension(letterSpacing.base);
    }
  }

  if (color) {
    // Use colorValueToSolidCss for text color (gradients not supported for text)
    styles.color = colorValueToSolidCss(color) || color;
  }

  if (textAlign) {
    // Support both plain string and responsive object
    styles.textAlign = typeof textAlign === 'string' ? textAlign : textAlign?.base || 'left';
  }

  if (textDecoration) {
    // Support both plain string and responsive object
    styles.textDecoration = typeof textDecoration === 'string' ? textDecoration : textDecoration?.base || 'none';
  }

  if (textTransform && textTransform !== 'none') {
    styles.textTransform = textTransform;
  }

  return styles;
}

/**
 * Convert background props to CSS properties
 * @param {Object} props - Component props containing background properties
 * @returns {Object} CSS style object
 */
export function resolveBackground(props) {
  const styles = {};

  const { backgroundColor, backgroundImageUrl } = props;

  // Handle background color/gradient
  if (backgroundColor && backgroundColor !== 'transparent') {
    // Use colorValueToCss to support gradients
    const bgCss = colorValueToCss(backgroundColor);
    if (bgCss && bgCss !== 'transparent') {
      // Check if it's a gradient (contains 'gradient')
      if (typeof bgCss === 'string' && bgCss.includes('gradient')) {
        // For gradients, use 'background' property
        styles.background = bgCss;
      } else {
        styles.backgroundColor = bgCss;
      }
    }
  }

  // Background image takes precedence
  if (backgroundImageUrl) {
    styles.backgroundImage = `url(${backgroundImageUrl})`;
    styles.backgroundSize = 'cover';
    styles.backgroundPosition = 'center';
    styles.backgroundRepeat = 'no-repeat';
    // If there's a background image, we can't use gradient, so use solid color fallback
    if (backgroundColor && backgroundColor !== 'transparent') {
      const solidBg = colorValueToSolidCss(backgroundColor);
      if (solidBg && solidBg !== 'transparent') {
        styles.backgroundColor = solidBg;
      }
    }
  }

  return styles;
}

/**
 * Convert positioning props to CSS properties
 * @param {Object} props - Component props containing positioning properties
 * @param {string} breakpoint - Current breakpoint (base/tablet/desktop)
 * @returns {Object} CSS style object
 */
export function resolvePositioning(props, breakpoint = 'base') {
  const styles = {};

  // Position
  if (props.position && props.position !== 'static') {
    styles.position = props.position;

    // Position offsets (only apply if position is not static)
    if (props.top) {
      const top = getResponsiveDimension(props.top, breakpoint);
      if (top !== 'auto') styles.top = top;
    }
    if (props.right) {
      const right = getResponsiveDimension(props.right, breakpoint);
      if (right !== 'auto') styles.right = right;
    }
    if (props.bottom) {
      const bottom = getResponsiveDimension(props.bottom, breakpoint);
      if (bottom !== 'auto') styles.bottom = bottom;
    }
    if (props.left) {
      const left = getResponsiveDimension(props.left, breakpoint);
      if (left !== 'auto') styles.left = left;
    }
  }

  // Z-Index
  if (props.zIndex !== undefined && props.zIndex !== 'auto' && props.zIndex !== '') {
    styles.zIndex = parseInt(props.zIndex, 10);
  }

  return styles;
}

/**
 * Resolve base styles common to ALL component types.
 * Handles: spacing, border, background, sizing, positioning, opacity,
 * filter, overflow, and flex child. Does NOT handle component-specific
 * styles (typography, display mode, etc.) — those come from each
 * component definition's resolveStyles() function.
 *
 * @param {Object} props - Merged props (defaults + user overrides)
 * @param {string} breakpoint - Current breakpoint (base/tablet/desktop)
 * @returns {Object} CSS style object with base styles
 */
export function resolveBaseStyles(props, breakpoint = 'base') {
  const styles = {};

  // Spacing (with breakpoint support)
  if (props.padding) {
    Object.assign(styles, resolveSpacing(props.padding, 'padding', breakpoint));
  }
  if (props.margin) {
    Object.assign(styles, resolveSpacing(props.margin, 'margin', breakpoint));
  }

  // Border
  Object.assign(styles, resolveBorder(props));

  // Background
  Object.assign(styles, resolveBackground(props));

  // Dimensions (width, height, minWidth, minHeight, maxWidth, maxHeight)
  Object.assign(styles, resolveSizing(props, breakpoint));

  // Opacity
  if (props.opacity !== undefined && props.opacity !== 100) {
    styles.opacity = props.opacity / 100;
  }

  // Filter
  if (props.filter && props.filter !== 'none') {
    styles.filter = props.filter;
  }

  // Positioning
  Object.assign(styles, resolvePositioning(props, breakpoint));

  // Overflow
  if (props.overflow && props.overflow !== 'visible') {
    styles.overflow = props.overflow;
  }

  // Flex child sizing (when inside a flex parent)
  if (props.flexChild) {
    Object.assign(styles, getFlexChildStyles(props.flexChild, breakpoint));
  }

  return styles;
}

/**
 * Resolve ALL styles for a component: base styles + component-specific styles.
 *
 * 1. Merges defaultProps from the component definition with user props
 * 2. Calls resolveBaseStyles() for universal properties
 * 3. Calls the component definition's resolveStyles() for type-specific properties
 * 4. Component-specific styles override base styles where they overlap
 *
 * @param {Object} component - Component instance { type, props }
 * @param {string} breakpoint - Current breakpoint (base/tablet/desktop)
 * @returns {Object} Complete CSS style object
 */
export function resolveAllStyles(component, breakpoint = 'base') {
  const definition = getComponentDefinition(component.type);
  const defaultProps = definition?.defaultProps || {};
  const props = { ...defaultProps, ...component.props };

  // Base styles (universal to all components)
  const baseStyles = resolveBaseStyles(props, breakpoint);

  // Component-specific styles (typography, display mode, flex layout, etc.)
  let componentStyles = {};
  if (definition?.resolveStyles) {
    componentStyles = definition.resolveStyles(props, breakpoint);
  }

  // Component-specific styles override base where they overlap
  return { ...baseStyles, ...componentStyles };
}

/**
 * Resolve all styles for a component (legacy API, delegates to resolveAllStyles)
 * @param {Object} component - Component instance { type, props }
 * @param {string} breakpoint - Current breakpoint (base/tablet/desktop)
 * @returns {Object} Complete CSS style object
 */
export function resolveComponentStyles(component, breakpoint = 'base') {
  return resolveAllStyles(component, breakpoint);
}

/**
 * Generate CSS media queries for responsive styles
 * @param {Object} component - Component instance
 * @param {string} componentId - Unique ID for CSS selector
 * @param {string|null} breakpoint - Forced breakpoint (null = normal responsive, 'base'/'tablet'/'desktop' = preview mode)
 * @returns {string} CSS string with media queries
 */
export function generateResponsiveCSS(component, componentId, breakpoint = null) {
  const props = component.props || {};
  const cssRules = [];

  // When a specific breakpoint is forced (preview mode), skip media queries
  // that would override the inline styles for that breakpoint.
  // - If breakpoint is 'base' (mobile): skip ALL media queries (they'd override mobile styles)
  // - If breakpoint is 'tablet': skip desktop media queries only
  // - If breakpoint is 'desktop' or null: generate all media queries (normal behavior)
  const skipTabletQueries = breakpoint === 'base';
  const skipDesktopQueries = breakpoint === 'base' || breakpoint === 'tablet';

  // Check for responsive width
  if (props.width?.tablet || props.width?.desktop) {
    if (props.width.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { width: ${formatDimension(props.width.tablet)} !important; }
        }
      `);
    }
    if (props.width.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { width: ${formatDimension(props.width.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive height (for spacer and other components)
  if (props.height?.tablet || props.height?.desktop) {
    if (props.height.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { height: ${formatDimension(props.height.tablet)} !important; }
        }
      `);
    }
    if (props.height.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { height: ${formatDimension(props.height.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive minWidth
  if (props.minWidth?.tablet || props.minWidth?.desktop) {
    if (props.minWidth.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { min-width: ${formatDimension(props.minWidth.tablet)} !important; }
        }
      `);
    }
    if (props.minWidth.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { min-width: ${formatDimension(props.minWidth.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive minHeight
  if (props.minHeight?.tablet || props.minHeight?.desktop) {
    if (props.minHeight.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { min-height: ${formatDimension(props.minHeight.tablet)} !important; }
        }
      `);
    }
    if (props.minHeight.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { min-height: ${formatDimension(props.minHeight.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive maxWidth
  if (props.maxWidth?.tablet || props.maxWidth?.desktop) {
    if (props.maxWidth.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { max-width: ${formatDimension(props.maxWidth.tablet)} !important; }
        }
      `);
    }
    if (props.maxWidth.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { max-width: ${formatDimension(props.maxWidth.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive maxHeight
  if (props.maxHeight?.tablet || props.maxHeight?.desktop) {
    if (props.maxHeight.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { max-height: ${formatDimension(props.maxHeight.tablet)} !important; }
        }
      `);
    }
    if (props.maxHeight.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { max-height: ${formatDimension(props.maxHeight.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive fontSize
  if (props.fontSize?.tablet || props.fontSize?.desktop) {
    if (props.fontSize.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { font-size: ${formatDimension(props.fontSize.tablet)} !important; }
        }
      `);
    }
    if (props.fontSize.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { font-size: ${formatDimension(props.fontSize.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive lineHeight
  if (props.lineHeight?.tablet || props.lineHeight?.desktop) {
    if (props.lineHeight.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { line-height: ${formatDimension(props.lineHeight.tablet)} !important; }
        }
      `);
    }
    if (props.lineHeight.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { line-height: ${formatDimension(props.lineHeight.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive letterSpacing
  if (props.letterSpacing?.tablet || props.letterSpacing?.desktop) {
    if (props.letterSpacing.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { letter-spacing: ${formatDimension(props.letterSpacing.tablet)} !important; }
        }
      `);
    }
    if (props.letterSpacing.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { letter-spacing: ${formatDimension(props.letterSpacing.desktop)} !important; }
        }
      `);
    }
  }

  // Check for responsive fontWeight
  if (typeof props.fontWeight === 'object' && props.fontWeight?.base !== undefined) {
    if (props.fontWeight.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { font-weight: ${props.fontWeight.tablet} !important; }
        }
      `);
    }
    if (props.fontWeight.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { font-weight: ${props.fontWeight.desktop} !important; }
        }
      `);
    }
  }

  // Check for responsive textAlign
  if (typeof props.textAlign === 'object' && props.textAlign?.base !== undefined) {
    if (props.textAlign.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { text-align: ${props.textAlign.tablet} !important; }
        }
      `);
    }
    if (props.textAlign.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { text-align: ${props.textAlign.desktop} !important; }
        }
      `);
    }
  }

  // Check for responsive textDecoration
  if (typeof props.textDecoration === 'object' && props.textDecoration?.base !== undefined) {
    if (props.textDecoration.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { text-decoration: ${props.textDecoration.tablet} !important; }
        }
      `);
    }
    if (props.textDecoration.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { text-decoration: ${props.textDecoration.desktop} !important; }
        }
      `);
    }
  }

  // Check for responsive direction (for rows)
  if (props.direction?.tablet || props.direction?.desktop) {
    if (props.direction.tablet && !skipTabletQueries) {
      const flexDir = props.direction.tablet === 'vertical' ? 'column' : 'row';
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { flex-direction: ${flexDir} !important; }
        }
      `);
    }
    if (props.direction.desktop && !skipDesktopQueries) {
      const flexDir = props.direction.desktop === 'vertical' ? 'column' : 'row';
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { flex-direction: ${flexDir} !important; }
        }
      `);
    }
  }

  // Check for responsive position offsets (top, right, bottom, left)
  const positionOffsets = ['top', 'right', 'bottom', 'left'];
  positionOffsets.forEach((offset) => {
    if (props[offset]?.tablet || props[offset]?.desktop) {
      if (props[offset].tablet && !skipTabletQueries) {
        const value = formatDimension(props[offset].tablet);
        if (value !== 'auto') {
          cssRules.push(`
            @media (min-width: 768px) {
              #${componentId} { ${offset}: ${value} !important; }
            }
          `);
        }
      }
      if (props[offset].desktop && !skipDesktopQueries) {
        const value = formatDimension(props[offset].desktop);
        if (value !== 'auto') {
          cssRules.push(`
            @media (min-width: 1024px) {
              #${componentId} { ${offset}: ${value} !important; }
            }
          `);
        }
      }
    }
  });

  // Check for responsive padding
  if (props.padding?.base !== undefined) {
    if (props.padding.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { ${formatSpacingCSS(props.padding.tablet, 'padding')} }
        }
      `);
    }
    if (props.padding.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { ${formatSpacingCSS(props.padding.desktop, 'padding')} }
        }
      `);
    }
  }

  // Check for responsive margin
  if (props.margin?.base !== undefined) {
    if (props.margin.tablet && !skipTabletQueries) {
      cssRules.push(`
        @media (min-width: 768px) {
          #${componentId} { ${formatSpacingCSS(props.margin.tablet, 'margin')} }
        }
      `);
    }
    if (props.margin.desktop && !skipDesktopQueries) {
      cssRules.push(`
        @media (min-width: 1024px) {
          #${componentId} { ${formatSpacingCSS(props.margin.desktop, 'margin')} }
        }
      `);
    }
  }

  // Check for responsive column span (for columns in rows)
  // Span data structure: { base: number, tablet: number|null, desktop: number|null }
  // span values are 1-12 for 12-column grid, converted to flex property
  if (component.type === 'column' && props.span !== undefined) {
    const span = props.span;

    // Helper to get flex CSS for a span value (with !important to override inline styles)
    const getFlexForSpan = (spanValue) => {
      if (spanValue === 12) {
        // Full width - use 100% basis to force new row
        return 'flex: 0 0 100% !important; max-width: 100% !important;';
      }
      // Use flex-grow proportional to span for flexible layouts
      return `flex: ${spanValue} 1 0% !important;`;
    };

    // Check if span is in responsive format
    if (typeof span === 'object' && span !== null && span.base !== undefined) {
      // Tablet override
      if (span.tablet && !skipTabletQueries) {
        cssRules.push(`
          @media (min-width: 768px) {
            #${componentId} { ${getFlexForSpan(span.tablet)} }
          }
        `);
      }
      // Desktop override
      if (span.desktop && !skipDesktopQueries) {
        cssRules.push(`
          @media (min-width: 1024px) {
            #${componentId} { ${getFlexForSpan(span.desktop)} }
          }
        `);
      }
    }
  }

  // Check for responsive flexChild
  // flexChild data structure: { base: { mode, basis }, tablet: { mode, basis }|null, desktop: { mode, basis }|null }
  if (props.flexChild?.base !== undefined) {
    const flexChildToCSS = (val) => {
      if (!val || !val.mode || val.mode === 'auto') return null;
      switch (val.mode) {
        case 'fill': return 'flex: 1 1 0% !important;';
        case 'hug': return 'flex: 0 0 auto !important;';
        case 'custom': return `flex: 0 0 ${val.basis || 50}% !important;`;
        default: return null;
      }
    };

    if (props.flexChild.tablet && !skipTabletQueries) {
      const css = flexChildToCSS(props.flexChild.tablet);
      if (css) {
        cssRules.push(`
          @media (min-width: 768px) {
            #${componentId} { ${css} }
          }
        `);
      }
    }
    if (props.flexChild.desktop && !skipDesktopQueries) {
      const css = flexChildToCSS(props.flexChild.desktop);
      if (css) {
        cssRules.push(`
          @media (min-width: 1024px) {
            #${componentId} { ${css} }
          }
        `);
      }
    }
  }

  // Check for responsive visibility
  // Visibility data structure: { base: bool, tablet: bool|null, desktop: bool|null }
  // true = visible, false = hidden, null = inherit from previous breakpoint
  if (props.visibility !== undefined) {
    const visibility = props.visibility;

    // Determine the display value to use when showing elements
    // Use flex for container types, block for others
    const isFlexContainer = ['row', 'column', 'container', 'section'].includes(component.type);
    const showDisplay = isFlexContainer ? 'flex' : 'block';

    // Base (mobile) visibility - if false, hide on mobile
    if (visibility.base === false) {
      cssRules.push(`
        #${componentId} { display: none !important; }
      `);
    }

    // Tablet visibility
    if (!skipTabletQueries) {
      if (visibility.tablet === false) {
        // Explicitly hidden on tablet
        cssRules.push(`
          @media (min-width: 768px) {
            #${componentId} { display: none !important; }
          }
        `);
      } else if (visibility.tablet === true && visibility.base === false) {
        // Explicitly shown on tablet, but was hidden on mobile
        cssRules.push(`
          @media (min-width: 768px) {
            #${componentId} { display: ${showDisplay} !important; }
          }
        `);
      }
    }

    // Desktop visibility
    if (!skipDesktopQueries) {
      // Determine if element would be hidden at tablet (either explicitly or inherited from base)
      const hiddenAtTablet = visibility.tablet === false ||
        (visibility.tablet === null && visibility.base === false);

      if (visibility.desktop === false) {
        // Explicitly hidden on desktop
        cssRules.push(`
          @media (min-width: 1024px) {
            #${componentId} { display: none !important; }
          }
        `);
      } else if (visibility.desktop === true && hiddenAtTablet) {
        // Explicitly shown on desktop, but was hidden at tablet breakpoint
        cssRules.push(`
          @media (min-width: 1024px) {
            #${componentId} { display: ${showDisplay} !important; }
          }
        `);
      }
    }
  }

  return cssRules.join('\n');
}

/**
 * Get gap size in pixels
 * @param {string} gap - Gap size key (none, sm, md, lg, xl)
 * @returns {string} CSS gap value
 */
export function getGapSize(gap) {
  return GAP_SIZES[gap] || GAP_SIZES.md;
}

const StyleResolver = {
  formatSpacingValue,
  formatDimension,
  formatSpacingCSS,
  getResponsiveDimension,
  getResponsiveDirection,
  getResponsiveSpacing,
  resolveSpacing,
  resolveBorder,
  resolveTypography,
  resolveBackground,
  resolvePositioning,
  resolveSizing,
  resolveBaseStyles,
  resolveAllStyles,
  resolveComponentStyles,
  generateResponsiveCSS,
  getGapSize,
};

export default StyleResolver;
